import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Trash2, HelpCircle } from 'lucide-react'
import HelpModal from './HelpModal'

// Словарь слов для генерации токена (вынесен наружу)
const WORD_BANK_ADJ = ['DRAGON', 'PHOENIX', 'TIGER', 'WOLF', 'EAGLE', 'STORM', 'THUNDER', 'LIGHTNING', 'FIRE', 'ICE', 'SHADOW', 'LIGHT', 'DARK', 'BRIGHT', 'MYSTIC', 'COSMIC', 'ROYAL', 'GOLDEN', 'SILVER', 'CRYSTAL']
const WORD_BANK_NOUN = ['WARRIOR', 'HUNTER', 'MASTER', 'LEGEND', 'HERO', 'CHAMPION', 'KNIGHT', 'GUARDIAN', 'SPIRIT', 'FORCE', 'POWER', 'ENERGY', 'BLADE', 'ARROW', 'SHIELD', 'CROWN', 'STAR', 'MOON', 'SUN', 'COMET']
const WORD_BANK_VERB = ['STRIKE', 'BLAST', 'RUSH', 'DASH', 'FLASH', 'BURST', 'CRUSH', 'SMASH', 'BREAK', 'RISE', 'FALL', 'JUMP', 'SPIN', 'TWIST', 'TURN', 'FLOW', 'WAVE', 'PULSE', 'GLOW', 'SHINE']

// Функция генерации seed из данных рисунка (вынесена наружу)
const generateSeedFromData = (drawingData) => {
  // Фильтруем маркеры разрыва пути, оставляем только точки с координатами
  const validPoints = drawingData.filter(point => !point.isBreak && point.x !== undefined && point.y !== undefined)
  
  if (validPoints.length === 0) {
    // Случайный seed
    const adjIndex = Math.floor(Math.random() * 20)
    const nounIndex = Math.floor(Math.random() * 20)
    const verbIndex = Math.floor(Math.random() * 20)
    const number = Math.floor(Math.random() * 10000)
    const shuffleIndex = Math.floor(Math.random() * 6)
    
    const words = [WORD_BANK_ADJ[adjIndex], WORD_BANK_NOUN[nounIndex], WORD_BANK_VERB[verbIndex]]
    const patterns = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]]
    const shuffledWords = patterns[shuffleIndex].map(i => words[i])
    
    return `${shuffledWords.join('-')}-${number.toString().padStart(4, '0')}`
  }
  
  // Seed на основе рисунка
  const hash = Math.floor(validPoints.reduce((acc, point) => acc + Math.round(point.x) + Math.round(point.y), 0))
  const adjIndex = Math.floor(Math.abs(hash) % 20)
  const nounIndex = Math.floor(Math.abs(hash * 7) % 20)
  const verbIndex = Math.floor(Math.abs(hash * 13) % 20)
  const number = Math.floor(Math.abs(hash) % 10000)
  const shuffleIndex = Math.floor(Math.abs(hash * 3) % 6)
  
  const words = [WORD_BANK_ADJ[adjIndex], WORD_BANK_NOUN[nounIndex], WORD_BANK_VERB[verbIndex]]
  const patterns = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]]
  const shuffledWords = patterns[shuffleIndex].map(i => words[i])
  
  return `${shuffledWords.join('-')}-${number.toString().padStart(4, '0')}`
}

// Глобальное хранилище для сохранения между открытиями (сбрасывается при перезагрузке)
let globalSavedSeed = ''
let globalSavedDrawingData = []

// Специальный маркер для обозначения разрыва пути (новая линия)
const PATH_BREAK = { isBreak: true }

const SeedEditor = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const canvasRef = useRef(null)
  const contextRef = useRef(null)
  const isDrawingRef = useRef(false) // Используем ref вместо state
  const lastUpdateTimeRef = useRef(0) // Время последнего обновления токена
  const updateSeedTimeoutRef = useRef(null) // Для финального обновления после остановки
  const lastPointRef = useRef(null) // Последняя точка для плавного рисования
  const [lineWidth] = useState(4) // Увеличиваем толщину для более мягких линий
  const [seed, setSeed] = useState(globalSavedSeed)
  const [drawingData, setDrawingData] = useState(globalSavedDrawingData)
  const [isUpdatingSeed, setIsUpdatingSeed] = useState(false) // Для анимации обновления
  const [showHelp, setShowHelp] = useState(false) // Для модалки справки
  const [showPlaceholder, setShowPlaceholder] = useState(true) // Показывать ли placeholder
  
  // Загружаем сохраненные данные при открытии модалки
  useEffect(() => {
    if (isOpen) {
      if (globalSavedSeed) {
        setSeed(globalSavedSeed)
      }
      if (globalSavedDrawingData.length > 0) {
        setDrawingData([...globalSavedDrawingData])
        setShowPlaceholder(false) // Скрываем placeholder если есть данные
      }
    }
  }, [isOpen])
  
  // Функция обновления токена с throttle (не чаще чем раз в 50ms)
  const updateSeedThrottled = useCallback((data) => {
    const now = Date.now()
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current
    
    // Если прошло меньше 50ms с последнего обновления, планируем обновление
    if (timeSinceLastUpdate < 50) {
      // Очищаем предыдущий отложенный таймер
      if (updateSeedTimeoutRef.current) {
        clearTimeout(updateSeedTimeoutRef.current)
      }
      
      // Планируем обновление через оставшееся время
      const remainingTime = 200 - timeSinceLastUpdate
      updateSeedTimeoutRef.current = setTimeout(() => {
        setSeed(generateSeedFromData(data))
        lastUpdateTimeRef.current = Date.now()
        setIsUpdatingSeed(false)
      }, remainingTime)
      
      setIsUpdatingSeed(true)
    } else {
      // Обновляем сразу, если прошло достаточно времени
      setSeed(generateSeedFromData(data))
      lastUpdateTimeRef.current = now
      
      // Короткая анимация
      setIsUpdatingSeed(true)
      setTimeout(() => setIsUpdatingSeed(false), 150)
    }
  }, [])

  // Обработчики рисования мышью
  const handleMouseDown = (e) => {
    if (!contextRef.current) return
    
    setShowPlaceholder(false) // Скрываем placeholder при первом касании
    isDrawingRef.current = true
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    // Сохраняем начальную точку
    lastPointRef.current = { x, y }
    
    const ctx = contextRef.current
    ctx.beginPath()
    ctx.moveTo(x, y)

    // Вибрация при начале рисования
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
    }
  }

  const handleMouseMove = (e) => {
    if (!isDrawingRef.current || !contextRef.current || !canvasRef.current) return

    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const ctx = contextRef.current
    
    // Используем quadraticCurveTo для плавных кривых
    if (lastPointRef.current) {
      const midX = (lastPointRef.current.x + x) / 2
      const midY = (lastPointRef.current.y + y) / 2
      ctx.quadraticCurveTo(lastPointRef.current.x, lastPointRef.current.y, midX, midY)
      ctx.stroke()
      lastPointRef.current = { x, y }
    }

    // Добавляем точку и обновляем seed с throttle
    setDrawingData(prev => {
      const newData = [...prev, { x, y }]
      updateSeedThrottled(newData)
      return newData
    })

    // Легкая вибрация при рисовании
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
    }
  }

  const handleMouseUp = () => {
    isDrawingRef.current = false
    lastPointRef.current = null
    
    // Добавляем маркер разрыва пути при окончании рисования
    setDrawingData(prev => [...prev, PATH_BREAK])
  }

  // Обработчики рисования касанием
  const handleTouchStart = useCallback((e) => {
    e.preventDefault()
    if (!contextRef.current) return
    
    setShowPlaceholder(false) // Скрываем placeholder при первом касании
    isDrawingRef.current = true
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const touch = e.touches[0]
    const x = touch.clientX - rect.left
    const y = touch.clientY - rect.top
    
    // Сохраняем начальную точку
    lastPointRef.current = { x, y }
    
    const ctx = contextRef.current
    ctx.beginPath()
    ctx.moveTo(x, y)

    // Вибрация при начале рисования
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
    }
  }, [])

  const handleTouchMove = useCallback((e) => {
    e.preventDefault()
    if (!isDrawingRef.current || !contextRef.current || !canvasRef.current) return

    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const touch = e.touches[0]
    const x = touch.clientX - rect.left
    const y = touch.clientY - rect.top

    const ctx = contextRef.current
    
    // Используем quadraticCurveTo для плавных кривых
    if (lastPointRef.current) {
      const midX = (lastPointRef.current.x + x) / 2
      const midY = (lastPointRef.current.y + y) / 2
      ctx.quadraticCurveTo(lastPointRef.current.x, lastPointRef.current.y, midX, midY)
      ctx.stroke()
      lastPointRef.current = { x, y }
    }

    // Добавляем точку и обновляем seed с throttle
    setDrawingData(prev => {
      const newData = [...prev, { x, y }]
      updateSeedThrottled(newData)
      return newData
    })

    // Легкая вибрация при рисовании
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
    }
  }, [updateSeedThrottled])

  const handleTouchEnd = useCallback(() => {
    isDrawingRef.current = false
    lastPointRef.current = null
    
    // Добавляем маркер разрыва пути при окончании рисования
    setDrawingData(prev => [...prev, PATH_BREAK])
  }, [])

  // Функция перерисовки canvas из сохраненных данных
  const redrawCanvas = useCallback((data) => {
    if (!contextRef.current || !canvasRef.current || data.length === 0) return
    
    const ctx = contextRef.current
    const canvas = canvasRef.current
    
    // Очищаем canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Рисуем сохраненные линии с учетом разрывов
    let currentPath = []
    
    for (let i = 0; i < data.length; i++) {
      const point = data[i]
      
      // Проверяем маркер разрыва пути
      if (point.isBreak) {
        // Рисуем накопленный путь
        if (currentPath.length > 0) {
          ctx.beginPath()
          ctx.moveTo(currentPath[0].x, currentPath[0].y)
          
          for (let j = 1; j < currentPath.length; j++) {
            const prevPoint = currentPath[j - 1]
            const currPoint = currentPath[j]
            const midX = (prevPoint.x + currPoint.x) / 2
            const midY = (prevPoint.y + currPoint.y) / 2
            ctx.quadraticCurveTo(prevPoint.x, prevPoint.y, midX, midY)
          }
          
          ctx.stroke()
          currentPath = []
        }
      } else {
        // Добавляем точку в текущий путь
        currentPath.push(point)
      }
    }
    
    // Рисуем последний путь, если он не был завершен маркером
    if (currentPath.length > 0) {
      ctx.beginPath()
      ctx.moveTo(currentPath[0].x, currentPath[0].y)
      
      for (let j = 1; j < currentPath.length; j++) {
        const prevPoint = currentPath[j - 1]
        const currPoint = currentPath[j]
        const midX = (prevPoint.x + currPoint.x) / 2
        const midY = (prevPoint.y + currPoint.y) / 2
        ctx.quadraticCurveTo(prevPoint.x, prevPoint.y, midX, midY)
      }
      
      ctx.stroke()
    }
  }, [])

  // Очистка canvas
  const handleClear = () => {
    if (!contextRef.current || !canvasRef.current) return
    
    const ctx = contextRef.current
    const canvas = canvasRef.current
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Очищаем данные и генерируем новый случайный seed
    const emptyData = []
    setDrawingData(emptyData)
    const newSeed = generateSeedFromData(emptyData)
    setSeed(newSeed)
    
    // Показываем placeholder снова
    setShowPlaceholder(true)
    
    // Сохраняем в глобальное хранилище
    globalSavedSeed = newSeed
    globalSavedDrawingData = emptyData

    // Вибрация при очистке
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.notificationOccurred('success')
    }
  }

  // Сохранение и закрытие
  const handleDone = () => {
    // Отправляем цель в Яндекс.Метрику
    if (window.ym) {
      window.ym(104258304, 'reachGoal', 'edit-seed-token')
    }
    
    // Сохраняем текущие данные в глобальные переменные
    globalSavedSeed = seed
    globalSavedDrawingData = [...drawingData]
    
    // Закрываем модалку
    onClose()
  }

  // Инициализация canvas при открытии
  useEffect(() => {
    if (isOpen && canvasRef.current) {
      // Небольшая задержка для правильного рендера
      setTimeout(() => {
        if (!canvasRef.current) return
        
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        
        // Устанавливаем размер canvas под размер контейнера
        const container = canvas.parentElement
        const containerWidth = container?.clientWidth || 400
        const size = Math.min(containerWidth - 32, 400)
        canvas.width = size
        canvas.height = size

        // Очищаем canvas темным фоном
        ctx.fillStyle = '#1a1a2e'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // Настраиваем контекст для рисования и сохраняем в ref
        ctx.strokeStyle = '#00FF7F'
        ctx.lineWidth = lineWidth
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        
        // Добавляем сглаживание и мягкость
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        // Добавляем shadow для более мягких линий
        ctx.shadowBlur = 2
        ctx.shadowColor = '#00FF7F'
        
        contextRef.current = ctx

        // Если есть сохраненный рисунок, восстанавливаем его
        if (drawingData.length > 0) {
          redrawCanvas(drawingData)
        } else if (!seed) {
          // Генерируем начальный случайный seed только если его еще нет
          const initialSeed = generateSeedFromData([])
          setSeed(initialSeed)
        }

        // Добавляем touch события с { passive: false }
        canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
        canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
        canvas.addEventListener('touchend', handleTouchEnd, { passive: false })
      }, 100)

      // Очистка при размонтировании
      return () => {
        if (canvasRef.current) {
          const canvas = canvasRef.current
          canvas.removeEventListener('touchstart', handleTouchStart)
          canvas.removeEventListener('touchmove', handleTouchMove)
          canvas.removeEventListener('touchend', handleTouchEnd)
        }
        // Очищаем таймер обновления токена
        if (updateSeedTimeoutRef.current) {
          clearTimeout(updateSeedTimeoutRef.current)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm ">
        <div className="bg-dark-primary rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto mb-0">
          {/* Header */}
          <div className="flex items-center justify-between p-4 pt-0">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-white">
                {t('seedEditor.title')}
              </h2>
              <button
                onClick={() => setShowHelp(true)}
                className="p-1.5 rounded-lg bg-surface/50 hover:bg-surface transition-all"
                title={t('seedEditor.helpTitle')}
              >
                <HelpCircle size={18} className="text-accent-primary" />
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-surface hover:bg-opacity-80 transition-all"
            >
              <X size={20} className="text-white" />
            </button>
          </div>

        {/* Canvas */}
        <div className="px-4 pb-4">
          <div className="relative bg-dark-secondary rounded-lg p-4">
            <canvas
              ref={canvasRef}
              className="w-full rounded-lg cursor-crosshair touch-none"
              style={{ backgroundColor: '#1a1a2e', minHeight: '300px' }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
            
            {/* Animated Placeholder - показывается только когда canvas пустой */}
            {showPlaceholder && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  {/* SVG анимация волны */}
                  <svg
                    width="200"
                    height="100"
                    viewBox="0 0 200 100"
                    className="mb-4 opacity-30"
                  >
                    <path
                      d="M 10 50 Q 40 20, 70 50 T 130 50 T 190 50"
                      fill="none"
                      stroke="#00FF7F"
                      strokeWidth="3"
                      strokeLinecap="round"
                    >
                      <animate
                        attributeName="stroke-dasharray"
                        from="0 1000"
                        to="1000 0"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        values="0.3;1;0.3"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                    </path>
                  </svg>
                  
                  {/* Текстовая подсказка */}
                  <p className="text-text-secondary text-sm animate-pulse">
                    {t('seedEditor.drawHint')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Generated Seed Display */}
        <div className="px-4 pb-4">
          <div className={`bg-surface rounded-lg p-4 border transition-all duration-300 ${
            isUpdatingSeed 
              ? 'border-accent-primary/50 shadow-lg shadow-accent-primary/20' 
              : 'border-accent-primary/20'
          }`}>
            <div className="text-xs text-text-secondary mb-2 text-center">
              {t('seedEditor.generatedSeed')}
            </div>
            <div className={`text-center font-mono text-accent-primary font-bold text-base whitespace-nowrap overflow-x-auto transition-opacity duration-200 ${
              isUpdatingSeed ? 'opacity-50' : 'opacity-100'
            }`}>
              {seed}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t border-white/10 flex gap-3">
          <button
            onClick={handleClear}
            className="flex-1 py-3 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 font-semibold transition-all flex items-center justify-center gap-2"
          >
            <Trash2 size={18} />
            {t('seedEditor.clear')}
          </button>
          <button
            onClick={handleDone}
            className="flex-1 py-3 rounded-lg bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold text-lg transition-all hover:opacity-90"
          >
            {t('seedEditor.done')}
          </button>
        </div>
      </div>
    </div>

    {/* Help Modal */}
    <HelpModal
      isOpen={showHelp}
      onClose={() => setShowHelp(false)}
      title={t('seedEditor.helpTitle')}
      steps={[
        t('seedEditor.helpText1'),
        t('seedEditor.helpText2'),
        t('seedEditor.helpText3'),
        t('seedEditor.helpText4')
      ]}
      buttonText={t('seedEditor.helpGotIt')}
    />
    </>
  )
}

// Функция для сброса сохраненного токена (вызывается после открытия кейса)
export const resetSavedSeed = () => {
  globalSavedSeed = ''
  globalSavedDrawingData = []
}

export default SeedEditor
